import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Loader2, Trash2, Cloud, AlertCircle, Play } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VideoUploader = ({
  value = '',
  onChange,
  folder = 'videos',
  label = 'Video',
  previewHeight = 200,
  className = ''
}) => {
  const { token } = useAdminAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    formData.append('resource_type', 'video');

    const response = await fetch(`${API_URL}/api/admin/cloudinary/upload-video`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Error al subir video');
    }

    const data = await response.json();
    return data.video.url;
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    if (!file.type.startsWith('video/')) {
      setError('Por favor selecciona un archivo de video');
      return;
    }

    // Validate file size (max 100MB for videos)
    if (file.size > 100 * 1024 * 1024) {
      setError('El video no debe superar 100MB');
      return;
    }

    setError(null);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress for better UX (Cloudinary doesn't provide real progress)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      const url = await uploadToCloudinary(file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (onChange) {
        onChange(url);
      }
      toast.success('Video subido a Cloudinary');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    handleFiles(Array.from(files));
  }, []);

  const handleInputChange = (e) => {
    const files = e.target.files;
    handleFiles(Array.from(files));
    e.target.value = '';
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const removeVideo = () => {
    if (onChange) {
      onChange('');
    }
  };

  const isCloudinaryUrl = (url) => url && url.includes('cloudinary.com');

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
        {label}
        <Cloud size={14} className="text-amber-500" title="Almacenamiento en la nube" />
      </label>

      {/* Video Preview */}
      {value && (
        <div className="relative mb-4 group">
          <div 
            className="relative overflow-hidden rounded-lg bg-gray-900"
            style={{ height: previewHeight }}
          >
            <video 
              src={value} 
              className="w-full h-full object-cover"
              autoPlay
              loop
              muted
              playsInline
            />
            {isCloudinaryUrl(value) && (
              <div className="absolute top-2 left-2 bg-amber-500/90 px-2 py-1 rounded text-xs text-black font-medium flex items-center gap-1">
                <Cloud size={12} /> Cloudinary
              </div>
            )}
            <div className="absolute top-2 right-2 bg-black/70 px-2 py-1 rounded text-xs text-white flex items-center gap-1">
              <Play size={12} /> Video
            </div>
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={handleClick}
                className="p-2 bg-amber-500 rounded-lg text-black hover:bg-amber-600 transition-colors"
                title="Cambiar video"
              >
                <Upload size={20} />
              </button>
              <button
                type="button"
                onClick={removeVideo}
                className="p-2 bg-red-500 rounded-lg text-white hover:bg-red-600 transition-colors"
                title="Eliminar"
              >
                <Trash2 size={20} />
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2 truncate">{value}</p>
        </div>
      )}

      {/* Upload Zone */}
      {!value && (
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
            ${isDragging 
              ? 'border-amber-500 bg-amber-500/10' 
              : 'border-gray-600 hover:border-gray-500 hover:bg-gray-700/30'
            }
            ${error ? 'border-red-500/50' : ''}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            onChange={handleInputChange}
            className="hidden"
            data-testid="video-upload-input"
          />

          {isUploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="text-amber-500 animate-spin" size={40} />
              <div className="w-48 h-2 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-amber-500 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-sm text-gray-400">Subiendo video a Cloudinary... {Math.round(uploadProgress)}%</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className={`p-4 rounded-full ${isDragging ? 'bg-amber-500/20' : 'bg-gray-700'}`}>
                <Play className={isDragging ? 'text-amber-500' : 'text-gray-400'} size={32} />
              </div>
              <div>
                <p className="text-sm text-white font-medium">
                  {isDragging ? 'Suelta el video aquí' : 'Arrastra un video o haz clic para seleccionar'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  MP4, WebM o MOV. Máximo 100MB. <span className="text-amber-500">Almacenamiento en la nube</span>
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 flex items-center justify-center gap-2 text-red-400">
              <AlertCircle size={16} />
              <span className="text-sm">{error}</span>
            </div>
          )}
        </div>
      )}

      {/* URL Input (fallback) */}
      <div className="mt-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex-1 h-px bg-gray-700" />
          <span className="text-xs text-gray-500">o ingresa URL del video</span>
          <div className="flex-1 h-px bg-gray-700" />
        </div>
        <input
          type="url"
          value={value}
          onChange={(e) => onChange && onChange(e.target.value)}
          placeholder="https://res.cloudinary.com/.../video.mp4"
          className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white placeholder-gray-400 focus:outline-none focus:border-amber-500 text-sm"
          data-testid="video-url-input"
        />
      </div>
    </div>
  );
};

export default VideoUploader;
